import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Form, Input, InputNumber, Select, Button, Tabs, message as messageApi} from 'antd';
import EditableDrawer from '../../../../components/EditableDrawer';
import {WarehouseOption} from '../../../../hooks/useWarehouseOptions';
import { DspType } from '../../../../graphql/types/DspType';
import { useTranslation } from 'react-i18next';
import { formatTimestampToDateTime } from '../../../../utils/dateTimeUtils';
import { canManageDspSettlement } from "../../../../utils/auth";
import { useAllSettlementTrackingPoints } from '../../channels/graphql/queries/getAllSettlementTrackingPoints';
import DspFinanceInfoForm, { DspFinanceInfoFormValues } from '../../../../components/dsp/DspFinanceInfoForm';
import { useDspFinanceInfoLazy } from '../../../../graphql/queries/dspFinanceInfo';
import { useSaveDspFinanceInfo } from '../../../../graphql/mutations/dspFinanceInfo';
import { DspFinanceInfoType } from '../../../../graphql/types/DspFinanceInfoType';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { showError } from 'utils/exceptionUtils';

type DspTabKey = 'basicInfo' | 'financeInfo';

/** 与 dsp 表 DDL 默认值保持一致 */
const DEFAULT_PAYMENT_TERM_DAYS = 12;
const DEFAULT_EXIT_DELAY_DAYS = 10;

interface DspFormDrawerProps {
    warehousesOptions: WarehouseOption[];
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: DspType & {
        openSettlementConfig?: boolean;
        settlementTrackingPoint?: string;
        billingCycleConfiguration?: number;
        settlementMode?: number;
        billType?: number[];
        timeZoneIana?: string;
        billUnitType?: number;
    }) => void | Promise<DspType | void>;
    dsp: DspType | null;
    mode: 'create' | 'edit';
}

const DspFormDrawer: React.FC<DspFormDrawerProps> = ({
    warehousesOptions,
                                                         visible,
                                                         onClose,
                                                         onSubmit,
                                                         dsp,
                                                         mode
                                                     }) => {
    const [form] = Form.useForm();
    const [financeForm] = Form.useForm<DspFinanceInfoFormValues>();
    const {t} = useTranslation();
    const [activeTab, setActiveTab] = useState<DspTabKey>('basicInfo');
    const [financeInfo, setFinanceInfo] = useState<DspFinanceInfoType | null>(null);
    const canCheckFinance = hasResource([
        ResourceCode.dsp.DSP_FINANCE_INFO,
        ResourceCode.dsp.DSP_FINANCE_INFO_OPERATE
    ]);
    const canOperateFinance = hasResource([ResourceCode.dsp.DSP_FINANCE_INFO_OPERATE]);
    const [loadFinanceInfo, {loading: financeLoading}] = useDspFinanceInfoLazy();
    const [saveDspFinanceInfo] = useSaveDspFinanceInfo();
    const title = mode === 'create' ? t('central.dsp.pageDetails.new') : t('central.dsp.pageDetails.dspDetails');
    const canManageSettlement = canManageDspSettlement();
    const [showSettlementConfig, setShowSettlementConfig] = useState(false);
    const [isTimeZoneLocked, setIsTimeZoneLocked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    // 转换warehouse数据为Select选项
    const warehouseOptions = warehousesOptions.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    })) || [];

    // 将 settlementTrackingPoints 转换为 options
    const settlementTrackingPoint = [
        { value: "60", label: "DELIVERED"}
    ];

    // 结算周期配置选项
    const billingCycleOptions = [
        { value: 1, label: 'Nature Week' },
        { value: 2, label: 'Nature Month' },
        { value: 4, label: 'Nature Biweekly' }
    ];

    // 结算模式选项（多选）
    const settlementModeOptions = [
        { value: 0, label: 'Pay later by Bill' }
    ];

    // DSP 账单类型选项（多选）
    const billTypeOptions = [
        { value: 2, label: 'Dsp Delivery fee Bill' }
    ];

    // 时区选项
    const timeZoneOptions = [
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
        { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    ];

    useEffect(() => {
        if (!visible) {
            isSubmittingRef.current = false;
            setSubmitting(false);
            setIsTimeZoneLocked(false);
            setActiveTab('basicInfo');
            setFinanceInfo(null);
            financeForm.resetFields();
        }
    }, [visible, financeForm]);

    // 打开编辑抽屉时加载财务档案；新建时 DSP 尚未落库，无档案可加载，需清掉上一次残留值避免回填到新建表单
    useEffect(() => {
        if (!visible) {
            return;
        }
        if (mode !== 'edit' || !dsp?.id || !canCheckFinance) {
            setFinanceInfo(null);
            return;
        }
        loadFinanceInfo({variables: {dspId: String(dsp.id)}})
            .then(({data}) => {
                if (data?.dspFinanceInfo) {
                    setFinanceInfo(data.dspFinanceInfo);
                }
            })
            .catch(showError);
    }, [visible, mode, dsp?.id, canCheckFinance, loadFinanceInfo]);

    // Reset form when DSP data or mode changes
    useEffect(() => {
        if (visible) {
            form.resetFields();

            if (dsp) {
                // 编辑模式：使用DSP数据和warehouse关联数据
                const openSettlementConfig = dsp.openSettlementConfig || false;
                setShowSettlementConfig(openSettlementConfig);
                setIsTimeZoneLocked(!!dsp.timeZoneIana);
                form.setFieldsValue({
                    ...dsp,
                    createdAt: dsp.createdAt ? formatTimestampToDateTime(dsp.createdAt) : '',
                    warehouseIds: dsp.warehouses?.map(w => w.id) || dsp.warehouseIds,
                    openSettlementConfig: openSettlementConfig,
                    billUnitType: dsp.billUnitType ?? 0
                });
            } else {
                // 新建模式：设置默认值
                setShowSettlementConfig(false);
                setIsTimeZoneLocked(false);
                const today = new Date();
                const formattedDate = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

                form.setFieldsValue({
                    dspCode: '',
                    createdAt: formattedDate,
                    status: 1,
                    warehouseIds: [],
                    openSettlementConfig: false,
                    billUnitType: 0,
                    paymentTermDays: DEFAULT_PAYMENT_TERM_DAYS,
                    exitDelayDays: DEFAULT_EXIT_DELAY_DAYS
                });
            }
        }
    }, [visible, dsp, mode, form]);

    const handleSubmitBasicInfo = async (): Promise<DspType | void> => {
        const values = await form.validateFields();
        const payload = mode === 'create' && !values.id
            ? ({...values, id: ''} as DspType)
            : values;
        const result = onSubmit(payload);
        // 若父组件返回 Promise，等待完成后再重置，避免请求未完成时允许再次点击
        if (result != null && typeof (result as Promise<unknown>)?.then === 'function') {
            return await (result as Promise<DspType | void>);
        }
    };

    const submitFinanceInfo = async (dspId: string, values: DspFinanceInfoFormValues) => {
        const {data} = await saveDspFinanceInfo({
            variables: {
                dspId,
                input: {
                    vendorCountry: values.vendorCountry,
                    federalTaxClassification: values.federalTaxClassification,
                    taxIdType: values.taxIdType,
                    taxIdNumber: values.taxIdNumber,
                    bankName: values.bankName,
                    accountType: values.accountType,
                    accountNo: values.accountNo,
                    routingNo: values.routingNo,
                    vendorAddress: values.vendorAddress
                }
            }
        });
        if (data?.saveDspFinanceInfo) {
            setFinanceInfo(data.saveDspFinanceInfo);
        }
    };

    const handleSubmitFinanceInfo = async () => {
        const values = await financeForm.validateFields();

        // 编辑态 DSP 已落库，直接存档案
        if (dsp?.id) {
            await submitFinanceInfo(String(dsp.id), values);
            messageApi.success(t('dspFinanceInfo.messages.saveSuccess') as string);
            return;
        }

        // 新建态财务档案无 dspId 可挂：先建 DSP 拿 dspId，再存档案（两个表单的必填项都已校验通过才会走到这）
        const savedDsp = await handleSubmitBasicInfo();
        if (!savedDsp?.id) {
            messageApi.warning(t('dspFinanceInfo.messages.saveDspFirst') as string);
            return;
        }
        try {
            await submitFinanceInfo(String(savedDsp.id), values);
        } catch (error) {
            // DSP 已建成，仅财务档案失败：引导去编辑页补充，避免用户在新建态重复提交撞 DSP 重名校验
            console.log('Save finance info failed after DSP created:', error);
            messageApi.warning(t('dspFinanceInfo.messages.financeFailedAfterDspCreated') as string);
            return;
        }
        messageApi.success(t('dspFinanceInfo.messages.saveSuccess') as string);
    };

    // 校验与提交都按当前 Tab 走：基础信息 Tab 不校验财务必填项，因此建 DSP 时财务信息可以不填
    const handleSubmit = async () => {
        if (submitting || isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        setSubmitting(true);
        try {
            if (activeTab === 'financeInfo') {
                await handleSubmitFinanceInfo();
            } else {
                await handleSubmitBasicInfo();
            }
            // 提交成功即关闭抽屉；失败会走 catch，不关闭
            onClose();
        } catch (info) {
            console.log('Validate Failed:', info);
        } finally {
            isSubmittingRef.current = false;
            setSubmitting(false);
        }
    };

    const basicInfoForm = (
            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelCol={{flex: '140px'}}
                wrapperCol={{flex: 1}}
                labelWrap
                initialValues={{  // 设置初始值
                    billUnitType: 0
                }}
            >
                {mode === 'edit' && (
                    <Form.Item
                        name="id"
                        label={t('central.dsp.pageDetails.dspId')}
                    >
                        <Input disabled={true}/>
                    </Form.Item>
                )}

                {mode === 'edit' && (
                    <Form.Item
                        name="dspCode"
                        label={t('central.dsp.pageDetails.dspCode')}
                    >
                        <Input disabled={true}/>
                    </Form.Item>
                )}

                <Form.Item
                    name="name"
                    label={t('central.dsp.pageDetails.companyName')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseInputCompanyName') as string}]}
                    extra={<span className="text-red-500 text-xs">{t('central.dsp.hints.companyNameHint')}</span>}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterCompanyName') as string}/>
                </Form.Item>

                <Form.Item
                    name="fullName"
                    label={t('central.dsp.pageDetails.fullName')}
                    rules={[
                        {required: true, message: t('central.dsp.messages.pleaseInputFullName') as string}
                    ]}
                    extra={<span className="text-red-500 text-xs">{t('central.dsp.hints.fullNameHint')}</span>}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterFullName') as string} maxLength={200} showCount/>
                </Form.Item>

                {mode === 'edit' && (
                    <Form.Item
                        name="createdAt"
                        label={t('central.dsp.pageDetails.creationDate')}
                    >
                        <Input disabled/>
                    </Form.Item>
                )}

                <Form.Item
                    name="contactName"
                    label={t('central.dsp.pageDetails.contactName')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseInputContactName') as string}]}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterContactName') as string}/>
                </Form.Item>

                <Form.Item
                    name="contactPhone"
                    label={t('central.dsp.pageDetails.phone')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseInputPhoneNumber') as string}]}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterPhoneNumber') as string}/>
                </Form.Item>

                <Form.Item
                    name="contactEmail"
                    label={t('central.dsp.pageDetails.email')}
                    rules={[
                        {required: true, message: t('central.dsp.messages.pleaseInputEmail') as string},
                        {type: 'email', message: t('central.dsp.messages.pleaseEnterAValidEmail') as string}
                    ]}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterEmailAddress') as string}/>
                </Form.Item>

                <Form.Item
                    name="operationEmail"
                    label={t('central.dsp.pageDetails.operationEmail')}
                    rules={[
                        {required: true, message: t('central.dsp.messages.pleaseInputOperationEmail') as string},
                        {type: 'email', message: t('central.dsp.messages.pleaseEnterAValidEmail') as string}
                    ]}
                >
                    <Input placeholder={t('central.dsp.placeholders.enterOperationEmail') as string}/>
                </Form.Item>

                <Form.Item
                    name="warehouseIds"
                    label={t('central.dsp.pageDetails.warehouse')}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('central.dsp.placeholders.selectWarehouses') as string}
                        options={warehouseOptions}
                        style={{ width: '100%' }}
                        className="[&_.ant-select-selection-overflow]:flex-col [&_.ant-select-selection-overflow-item]:w-full [&_.ant-select-selection-item]:w-full [&_.ant-select-selection-search]:!min-h-0 [&_.ant-select-selection-search]:!h-0 [&_.ant-select-selection-search-input]:!h-0 [&_.ant-select-selection-search-input]:!min-h-0"
                    />
                </Form.Item>

                <Form.Item
                    name="status"
                    label={t('central.dsp.pageDetails.status')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseSelectStatus') as string}]}
                >
                    <Select
                        placeholder={t('central.dsp.placeholders.selectStatus') as string}
                        options={[
                            {value: 1, label: t('enum.activeStatus.active')},
                            {value: 0, label: t('enum.activeStatus.suspended')},
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    name="paymentTermDays"
                    label={t('central.dsp.pageDetails.paymentTermDays')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseInputPaymentTermDays') as string}]}
                >
                    <InputNumber min={1} precision={0} style={{width: '100%'}}/>
                </Form.Item>

                <Form.Item
                    name="exitDelayDays"
                    label={t('central.dsp.pageDetails.exitDelayDays')}
                    rules={[{required: true, message: t('central.dsp.messages.pleaseInputExitDelayDays') as string}]}
                >
                    <InputNumber min={1} precision={0} style={{width: '100%'}}/>
                </Form.Item>

                {/*结算客户配置*/}
                {canManageSettlement && mode === 'edit' && (
                    <Form.Item
                        name="openSettlementConfig"
                        label={t('central.dsp.settlement.openConfigLabel')}
                        style={{ pointerEvents: 'none' }}
                    >
                        <div style={{ pointerEvents: 'auto' }}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    const newValue = !showSettlementConfig;
                                    setShowSettlementConfig(newValue);
                                    form.setFieldsValue({
                                        openSettlementConfig: newValue,
                                    });
                                }}
                                style={{marginBottom: 16}}
                            >
                                {showSettlementConfig ? '隐藏结算客户配置' : '开启结算客户配置'}
                            </Button>
                        </div>
                    </Form.Item>
                )}

                {canManageSettlement && mode === 'edit' && showSettlementConfig && (
                    <>
                        <Form.Item
                            name="settlementTrackingPoint"
                            label={t('central.dsp.settlement.settlementTrackingPoint')}
                            rules={[{required: true, message: 'please select tracking point'}]}
                        >
                            <Select
                                options={settlementTrackingPoint}
                                placeholder={'please select tracking point'}
                            />
                        </Form.Item>

                        <Form.Item
                            name="billingCycleConfiguration"
                            label={t('central.dsp.settlement.billingCycleConfiguration')}
                            rules={[{required: true, message: 'please select billing Cycle Configuration'}]}
                        >
                            <Select
                                options={billingCycleOptions}
                                placeholder={'please select billing Cycle Configuration'}
                            />
                        </Form.Item>

                        <Form.Item
                            name="settlementMode"
                            label={t('central.dsp.settlement.settlementMode')}
                            rules={[{required: true, message: 'please select Settlement Mode'}]}
                        >
                            <Select
                                options={settlementModeOptions}
                                placeholder={'please select Settlement Mode'}
                            />
                        </Form.Item>

                        <Form.Item
                            name="billType"
                            label={t('central.dsp.settlement.dspBillType')}
                            rules={[{required: true, message: 'please select DSP BillType'}]}
                        >
                            <Select
                                mode="multiple"
                                options={billTypeOptions}
                                placeholder={'please select DSP BillType'}
                            />
                        </Form.Item>

                        <Form.Item
                            name="timeZoneIana"
                            label={t('central.dsp.settlement.timeZone')}
                            rules={[{required: true, message: 'please select timeZone'}]}
                            extra={isTimeZoneLocked ? (
                                <span className="text-amber-600 text-xs">
                                    {t('central.dsp.settlement.timeZoneLockedHint')}
                                </span>
                            ) : undefined}
                        >
                            <Select
                                disabled={isTimeZoneLocked}
                                options={timeZoneOptions}
                                placeholder={'please select timeZone'}
                            />
                        </Form.Item>

                        <Form.Item
                            name="billUnitType"
                            label={t('central.dsp.settlement.billUnitType')}
                            rules={showSettlementConfig ? [{ required: true, message: 'Please select Bill Unit Type!' }] : []}
                        >
                            <Select
                                options={[
                                    { value: 0, label: 'English' },
                                    { value: 1, label: 'Metric' },
                                ]}
                                disabled={true}
                            />
                        </Form.Item>
                    </>
                )}
            </Form>
    );

    // 无 DSP 财务档案资源码时不展示财务信息；新建时也可填写，提交该 Tab 会先建 DSP 再存档案
    const showFinanceTab = canCheckFinance;

    return (
        <EditableDrawer
            title={title}
            onClose={onClose}
            visible={visible}
            width={600}
            onSubmit={handleSubmit}
            confirmDisabled={submitting || (activeTab === 'financeInfo' && !canOperateFinance)}
        >
            {showFinanceTab ? (
                <Tabs
                    activeKey={activeTab}
                    onChange={key => setActiveTab(key as DspTabKey)}
                    items={[
                        {
                            key: 'basicInfo',
                            label: t('dspFinanceInfo.tabs.basicInfo'),
                            children: basicInfoForm
                        },
                        {
                            key: 'financeInfo',
                            label: t('dspFinanceInfo.tabs.financeInfo'),
                            children: (
                                <DspFinanceInfoForm
                                    form={financeForm}
                                    financeInfo={financeInfo}
                                    disabled={!canOperateFinance || financeLoading}
                                />
                            )
                        }
                    ]}
                />
            ) : (
                basicInfoForm
            )}
        </EditableDrawer>
    );
};

export default DspFormDrawer; 